// Derive "request" rows for job technician assignments from jobs payload.
// This is used for the Requests badge count + the Requests screen.
import { getStatusLabel, isCancelledStatus, isInvoicedStatus } from './jobStatuses';
import { getPriorityLabel } from './jobPriorities';

export const TECH_STATUS_LABELS = {
  1: 'Dispatched',
  2: 'On route',
  3: 'Start',
  4: 'Paid',
  5: 'Request payment',
  6: 'No show',
  7: 'Reschedule',
  8: 'Need part',
  9: 'Free of charge',
};

export const FREE_OF_CHARGE_STATUS = 9;

/** Prompt whenever Free of charge is selected, including re-selecting it. */
export const needsFreeOfChargeConfirm = (newStatus) =>
  Number(newStatus) === FREE_OF_CHARGE_STATUS;

/** Solid chip palette for job_technicians.status (matches job status chip pattern). */
export const JOB_TECHNICIAN_CHIP_STYLES = {
  1: { bgColor: '#93c5fd', textColor: '#1e3a8a', borderColor: '#2563eb' },   // Dispatched — blue
  2: { bgColor: '#a5b4fc', textColor: '#312e81', borderColor: '#4f46e5' },   // On route — indigo
  3: { bgColor: '#86efac', textColor: '#14532d', borderColor: '#16a34a' },   // Start — green
  4: { bgColor: '#6ee7b7', textColor: '#064e3b', borderColor: '#059669' },   // Paid — emerald
  5: { bgColor: '#fda4af', textColor: '#881337', borderColor: '#e11d48' },   // Request payment — rose
  6: { bgColor: '#fca5a5', textColor: '#7f1d1d', borderColor: '#dc2626' },   // No show — red
  7: { bgColor: '#fdba74', textColor: '#7c2d12', borderColor: '#ea580c' },   // Reschedule — orange
  8: { bgColor: '#d8b4fe', textColor: '#581c87', borderColor: '#9333ea' },   // Need part — purple
  9: { bgColor: '#99f6e4', textColor: '#134e4a', borderColor: '#0d9488' },   // Free of charge — teal
};

const DEFAULT_TECHNICIAN_CHIP_STYLE = {
  bgColor: '#d1d5db',
  textColor: '#1f2937',
  borderColor: '#6b7280',
};

export const getJobTechnicianStatusLabel = (status) => {
  const numeric = Number(status);
  return TECH_STATUS_LABELS[numeric] || 'Dispatched';
};

/**
 * Solid chip styles for a job_technicians.status value.
 * @param {number} statusId
 * @returns {{ bgColor: string, textColor: string, borderColor: string }}
 */
export const getJobTechnicianChipStyles = (statusId) =>
  JOB_TECHNICIAN_CHIP_STYLES[Number(statusId)] || DEFAULT_TECHNICIAN_CHIP_STYLE;

export const getJobTechnicianDisplayName = (tech) => {
  if (!tech) return '';
  if (tech.type === 'vendor') return tech.company_name || '';
  return `${tech.first_name || ''} ${tech.last_name || ''}`.trim();
};

export const jobTechnicianNeedsAttention = (assignment) => {
  if (!assignment) return false;

  const status = Number(assignment.status);
  const isPaymentRequest = status === 5;
  const hasUnlockRequest = assignment.requested_unlock === true;

  return isPaymentRequest || hasUnlockRequest;
};

export const getJobTechnicianAttentionReasons = (assignment) => {
  if (!assignment) return [];
  const reasons = [];

  const status = Number(assignment.status);
  if (status === 5) reasons.push('Request payment');
  if (assignment.requested_unlock === true) reasons.push('Unlock requested');

  return reasons;
};

/**
 * Flatten jobs[*].technicians into request rows.
 *
 * @param {Array<any>} jobs - jobs from API (each job should include technicians array)
 * @returns {Array<any>} DataGrid rows (each row has stable `id`)
 */
export const deriveJobTechnicianRequestRows = (jobs = []) => {
  if (!Array.isArray(jobs) || jobs.length === 0) return [];

  const rows = [];
  for (const job of jobs) {
    if (isCancelledStatus(Number(job.status))) continue;

    const assignments = job?.technicians || [];
    if (!Array.isArray(assignments) || assignments.length === 0) continue;

    for (const assignment of assignments) {
      if (!jobTechnicianNeedsAttention(assignment)) continue;

      const technicianName = getJobTechnicianDisplayName(assignment);
      const reasons = getJobTechnicianAttentionReasons(assignment);

      rows.push({
        // DataGrid expects `id` to be unique across rows
        id: `${job.id}:${assignment.id}`,

        // Job context
        job_id: job.id,
        work_order: job.work_order,
        job_description: job.description,
        client_name: job.client_name,
        client_id: job.client_id,
        dispatcher: job.dispatcher,

        // Technician assignment fields
        assignment_id: assignment.id,
        technician_id: assignment.technician_id,
        technician_name: technicianName,
        technician_type: assignment.type,
        status: assignment.status,
        status_label: getJobTechnicianStatusLabel(assignment.status),
        scheduled_date: assignment.scheduled_date,
        requested_unlock: assignment.requested_unlock === true,

        // Convenience fields for UI
        attention_reasons: reasons,
        attention_reasons_text: reasons.join(', '),

        // Timestamps (if present)
        created_at: assignment.created_at,
        updated_at: assignment.updated_at,
      });
    }
  }

  return rows;
};

/**
 * Check if a job technician assignment is missing scheduled_date (visit date).
 *
 * @param {Object} assignment - job technician assignment
 * @returns {boolean} true if missing scheduled_date
 */
export const jobTechnicianMissingVisitDate = (assignment) => {
  if (!assignment) return false;

  const scheduledDate = assignment.scheduled_date;
  return !scheduledDate || scheduledDate === '' || scheduledDate === null;
};

/**
 * Get reasons why a job is flagged as unassigned/pending.
 *
 * @param {Object} job - job object with technicians array
 * @returns {Array<string>} list of reasons
 */
export const getUnassignedPendingJobReasons = (job) => {
  if (!job) return [];
  const reasons = [];

  const technicians = job.technicians || [];
  if (technicians.length === 0) {
    reasons.push('No technicians assigned');
    return reasons;
  }

  // Check if any technician is missing scheduled_date
  for (const tech of technicians) {
    const scheduledDate = tech.scheduled_date;
    const hasNoScheduledDate = !scheduledDate || scheduledDate === '' || scheduledDate === null;

    const techName = getJobTechnicianDisplayName(tech);
    if (hasNoScheduledDate) {
      reasons.push(`${techName || 'Tech'}: No visit date`);
    }
  }

  return reasons;
};

/**
 * Check if a job should be flagged as unassigned/pending.
 * A job is flagged if:
 * - It has 0 technicians assigned, OR
 * - Any technician is missing scheduled_date (visit date)
 * Excludes cancelled, Invoiced, and Invoiced/Loss jobs.
 *
 * @param {Object} job - job object with technicians array
 * @returns {boolean} true if job should be flagged
 */
export const jobIsUnassignedOrPending = (job) => {
  if (!job) return false;

  const statusId = Number(job.status);
  if (isCancelledStatus(statusId) || isInvoicedStatus(statusId)) return false;

  const technicians = job.technicians || [];
  // No technicians assigned
  if (technicians.length === 0) return true;

  // Check if any technician is missing scheduled_date
  for (const tech of technicians) {
    if (jobTechnicianMissingVisitDate(tech)) return true;
  }

  return false;
};

/**
 * Derive "unassigned/pending" job rows for the Requests screen.
 * Jobs are included if:
 * - They have 0 technicians assigned, OR
 * - Any technician is missing scheduled_date (visit date)
 * Excludes cancelled, Invoiced, and Invoiced/Loss jobs.
 *
 * @param {Array<any>} jobs - jobs from API (each job should include technicians array)
 * @returns {Array<any>} DataGrid rows (each row has stable `id`)
 */
export const deriveUnassignedPendingJobRows = (jobs = []) => {
  if (!Array.isArray(jobs) || jobs.length === 0) return [];

  const rows = [];
  for (const job of jobs) {
    if (!jobIsUnassignedOrPending(job)) continue;

    const reasons = getUnassignedPendingJobReasons(job);
    const technicians = job.technicians || [];

    rows.push({
      // DataGrid expects `id` to be unique across rows
      id: job.id,

      // Job fields
      job_id: job.id,
      work_order: job.work_order,
      client_name: job.client_name,
      client_id: job.client_id,
      status: job.status,
      status_label: getStatusLabel(Number(job.status)),
      priority: job.priority,
      priority_label: getPriorityLabel(job.priority),
      trade: job.trade,
      description: job.description,
      city: job.city,
      state: job.state,
      address: job.address,
      nte: job.nte,
      admin: job.admin,
      dispatcher: job.dispatcher,
      technician_count: technicians.length,
      created_at: job.created_at,
      updated_at: job.updated_at,

      // Convenience fields for UI
      attention_reasons: reasons,
      attention_reasons_text: reasons.join(', '),
    });
  }

  return rows;
};

/**
 * Derive "My Requests" rows — outgoing requests the current user has made.
 * Aggregates job-level unlock requests (matched by user id), proposal unlock/approval
 * requests (matched by email), part unlock/approval requests (matched by email),
 * and technician assignment unlock requests (matched by email).
 *
 * Non-management users only (management can unlock directly and never request).
 *
 * @param {Array<any>} jobs - jobs from API
 * @param {{ id: string, email: string }} currentUser - logged-in user
 * @returns {Array<any>} DataGrid rows with a `request_category` label
 */
export const deriveMyRequestRows = (jobs = [], currentUser = {}) => {
  if (!Array.isArray(jobs) || jobs.length === 0 || !currentUser) return [];

  const userId = currentUser.id;
  const userEmail = (currentUser.email || '').toLowerCase();
  if (!userId && !userEmail) return [];

  const rows = [];

  for (const job of jobs) {
    // 1) Job-level unlock request (requested_unlock_by is a UUID)
    if (job.requested_unlock_at && job.requested_unlock_by === userId) {
      rows.push({
        id: `my-job-unlock:${job.id}`,
        job_id: job.id,
        work_order: job.work_order || 'N/A',
        client_name: job.client_name,
        client_id: job.client_id,
        description: job.description,
        dispatcher: job.dispatcher,
        request_category: 'Job Unlock',
        request_detail: job.requested_unlock_reason || '',
        requested_at: job.requested_unlock_at,
        status: job.status,
        status_label: getStatusLabel(Number(job.status)),
      });
    }

    // 2) Proposal unlock / approval requests (requested_*_by is email)
    const proposals = job?.pendingProposals || [];
    for (const proposal of proposals) {
      const isUnlock = !!proposal.requested_unlock_at;
      // Ignore stale approval requests on already-approved proposals
      const isApproval = !!proposal.requested_approval_at && !proposal.is_approved;
      if (!isUnlock && !isApproval) continue;

      const requesterEmail = isUnlock
        ? (proposal.requested_unlock_by || '')
        : (proposal.requested_approval_by || '');
      if (requesterEmail.toLowerCase() !== userEmail) continue;

      const proposalTextTruncated = proposal.proposal_text
        ? (proposal.proposal_text.length > 50
            ? proposal.proposal_text.substring(0, 50) + '...'
            : proposal.proposal_text)
        : '';

      rows.push({
        id: `my-proposal:${proposal.id}`,
        job_id: job.id,
        work_order: job.work_order || 'N/A',
        client_name: job.client_name,
        client_id: job.client_id,
        description: job.description,
        dispatcher: job.dispatcher,
        request_category: isUnlock ? 'Proposal Unlock' : 'Proposal Approval',
        request_detail: proposalTextTruncated,
        requested_at: isUnlock ? proposal.requested_unlock_at : proposal.requested_approval_at,
        status: job.status,
        status_label: getStatusLabel(Number(job.status)),
      });
    }

    // 3) Part unlock / approval requests (requested_*_by is email)
    const parts = job?.partRequests || job?.parts || [];
    for (const part of parts) {
      const hasApproval = !!part.requested_approval_at;
      const hasUnlock = !!part.requested_unlock_at;
      if (!hasApproval && !hasUnlock) continue;

      const requesterEmail = hasApproval
        ? (part.requested_approval_by || '')
        : (part.requested_unlock_by || '');
      if (requesterEmail.toLowerCase() !== userEmail) continue;

      const requestTypes = [];
      if (hasApproval) requestTypes.push('Approval');
      if (hasUnlock) requestTypes.push('Unlock');

      rows.push({
        id: `my-part:${job.id}:${part.id}`,
        job_id: job.id,
        work_order: job.work_order || 'N/A',
        client_name: job.client_name,
        client_id: job.client_id,
        description: job.description,
        dispatcher: job.dispatcher,
        request_category: `Part ${requestTypes.join(' & ')}`,
        request_detail: part.name || '',
        requested_at: hasApproval ? part.requested_approval_at : part.requested_unlock_at,
        status: job.status,
        status_label: getStatusLabel(Number(job.status)),
      });
    }

    // 4) Technician assignment unlock requests (requested_unlock_by is email)
    const techs = job?.technicians || [];
    for (const tech of techs) {
      if (!tech.requested_unlock || tech.requested_unlock !== true) continue;
      if ((tech.requested_unlock_by || '').toLowerCase() !== userEmail) continue;

      rows.push({
        id: `my-tech-unlock:${job.id}:${tech.id}`,
        job_id: job.id,
        work_order: job.work_order || 'N/A',
        client_name: job.client_name,
        client_id: job.client_id,
        description: job.description,
        dispatcher: job.dispatcher,
        request_category: 'Tech Assignment Unlock',
        request_detail: getJobTechnicianDisplayName(tech),
        requested_at: tech.requested_unlock_at,
        status: job.status,
        status_label: getStatusLabel(Number(job.status)),
      });
    }
  }

  // Sort newest first
  rows.sort((a, b) => {
    const da = a.requested_at ? new Date(a.requested_at).getTime() : 0;
    const db = b.requested_at ? new Date(b.requested_at).getTime() : 0;
    return db - da;
  });

  return rows;
};

/**
 * Derive rows for jobs with a pending job-level unlock request.
 * Used for the Requests badge count + the Requests screen (Job Unlock Requests grid).
 * Visible to owner / general_manager only (gated in UI via canAccessJobUnlockRequestsTab).
 *
 * @param {Array<any>} jobs - jobs from API (jobs.* includes lock / unlock request fields)
 * @returns {Array<any>} DataGrid rows (each row has stable `id`)
 */
export const deriveJobUnlockRequestRows = (jobs = []) => {
  if (!Array.isArray(jobs) || jobs.length === 0) return [];

  const rows = [];
  for (const job of jobs) {
    if (!job?.requested_unlock_at) continue;

    rows.push({
      id: `job-unlock:${job.id}`,
      job_id: job.id,
      work_order: job.work_order || 'N/A',
      client_name: job.client_name,
      client_id: job.client_id,
      description: job.description,
      dispatcher: job.dispatcher,
      status: job.status,
      status_label: getStatusLabel(Number(job.status)),
      locked_reason_status: job.locked_reason_status,
      locked_reason_label: job.locked_reason_status != null
        ? getStatusLabel(Number(job.locked_reason_status))
        : '',
      is_locked: job.is_locked === true,
      locked_at: job.locked_at,
      requested_unlock_by: job.requested_unlock_by,
      requested_unlock_at: job.requested_unlock_at,
      requested_unlock_reason: job.requested_unlock_reason || '',
      created_at: job.created_at,
    });
  }

  return rows;
};

/**
 * Derive "proposal request" rows for jobs with pending approval or unlock requests.
 * Used for the Requests badge count + the Requests screen (Job Proposals grid).
 *
 * @param {Array<any>} jobs - jobs from API (each job should include pendingProposals array)
 * @returns {Array<any>} DataGrid rows (each row has stable `id`)
 */
export const deriveProposalRequestRows = (jobs = []) => {
  if (!Array.isArray(jobs) || jobs.length === 0) return [];

  const rows = [];
  for (const job of jobs) {
    if (isCancelledStatus(Number(job.status))) continue;

    const proposals = job?.pendingProposals || [];
    if (!Array.isArray(proposals) || proposals.length === 0) continue;

    for (const proposal of proposals) {
      // Determine request type - unlock takes precedence if both exist
      const isUnlockRequest = !!proposal.requested_unlock_at;
      // Ignore stale approval requests on already-approved proposals
      const isApprovalRequest = !!proposal.requested_approval_at && !proposal.is_approved;

      // Skip if no request (should not happen since backend filters, but safety check)
      if (!isUnlockRequest && !isApprovalRequest) continue;

      const requestType = isUnlockRequest ? 'Unlock Request' : 'Approval Request';
      const requestedBy = isUnlockRequest 
        ? proposal.requested_unlock_by 
        : proposal.requested_approval_by;
      const requestedAt = isUnlockRequest 
        ? proposal.requested_unlock_at 
        : proposal.requested_approval_at;

      // Truncate proposal text for display
      const proposalTextTruncated = proposal.proposal_text 
        ? (proposal.proposal_text.length > 50 
            ? proposal.proposal_text.substring(0, 50) + '...' 
            : proposal.proposal_text)
        : '';

      rows.push({
        // DataGrid expects `id` to be unique across rows
        id: proposal.id,

        // Job context
        job_id: job.id,
        work_order: job.work_order || 'N/A',
        client_name: job.client_name,
        client_id: job.client_id,
        job_description: job.description,
        dispatcher: job.dispatcher,

        // Proposal fields
        proposal_id: proposal.id,
        proposal_text: proposalTextTruncated,
        proposal_text_full: proposal.proposal_text,
        price: proposal.price,
        is_approved: proposal.is_approved,
        is_locked: proposal.is_locked,
        invoiced: proposal.invoiced,
        created_by: proposal.created_by,

        // Request info
        request_type: requestType,
        requested_by: requestedBy,
        requested_at: requestedAt,

        // Timestamps
        created_at: proposal.created_at,
      });
    }
  }

  return rows;
};

/**
 * Derive "part request" rows for jobs with parts that have approval or unlock requested.
 * Used for the Requests badge count + the Requests screen (Parts request accordion).
 * Expects jobs to have partRequests (from list API) or parts (from detail) with requested_approval_at / requested_unlock_at.
 *
 * @param {Array<any>} jobs - jobs from API (each job may have partRequests or parts array)
 * @returns {Array<any>} DataGrid rows (each row has stable `id`)
 */
export const derivePartRequestRows = (jobs = []) => {
  if (!Array.isArray(jobs) || jobs.length === 0) return [];

  const rows = [];
  for (const job of jobs) {
    if (isCancelledStatus(Number(job.status))) continue;

    const parts = job?.partRequests || job?.parts || [];
    if (!Array.isArray(parts) || parts.length === 0) continue;

    for (const part of parts) {
      const hasApprovalRequest = !!part.requested_approval_at;
      const hasUnlockRequest = !!part.requested_unlock_at;
      if (!hasApprovalRequest && !hasUnlockRequest) continue;

      const requestTypes = [];
      if (hasApprovalRequest) requestTypes.push('Approval Request');
      if (hasUnlockRequest) requestTypes.push('Unlock Request');
      const request_type = requestTypes.join(', ');
      const requestedBy = hasApprovalRequest ? part.requested_approval_by : part.requested_unlock_by;
      const requestedAt = hasApprovalRequest ? part.requested_approval_at : part.requested_unlock_at;

      rows.push({
        id: `part:${job.id}:${part.id}`,
        job_id: job.id,
        work_order: job.work_order || 'N/A',
        client_name: job.client_name,
        client_id: job.client_id,
        job_description: job.description,
        dispatcher: job.dispatcher,
        part_id: part.id,
        part_name: part.name,
        part_description: part.description,
        cost: part.cost,
        request_type,
        requested_by: requestedBy,
        requested_at: requestedAt,
        requested_approval_at: part.requested_approval_at,
        requested_unlock_at: part.requested_unlock_at,
        created_at: part.created_at,
      });
    }
  }

  return rows;
};

